/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo } from 'react';
import { useScapiFetcher } from '@/hooks/use-scapi-fetcher';

/**
 * Thumbnails for typeahead product suggestions (search-3a-comparison.md P2).
 *
 * ROOT CAUSE of the "No image available" bug: SCAPI's getSearchSuggestions
 * carries NO image data — its product entries are only {currency, price,
 * productId, productName}, and the endpoint ignores an `expand` parameter
 * (verified against bkcb-004, 2026-08-16). The suggestion panel's previews
 * therefore never had a URL to render. This hook backfills them with one
 * getProducts call for the suggested ids and maps each product to the first
 * image of its first image group (allImages:false → the representative
 * group). The panel renders names/prices immediately and images as this
 * resolves — live NM shows no products in its typeahead at all, so any
 * placeholder gap is already ours-extra territory.
 */
export function useSuggestionProductImages(productIds: string[]): Record<string, string> {
    const idsKey = productIds.join(',');

    const fetcher = useScapiFetcher('shopperProducts', 'getProducts', {
        params: {
            query: {
                ids: productIds,
                allImages: false,
            },
        },
    });

    useEffect(() => {
        if (idsKey.length > 0) {
            void fetcher.load();
        }
        // The fetcher object identity changes per render; the ids key is the real trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    return useMemo(() => {
        const map: Record<string, string> = {};
        if (idsKey.length === 0) return map;
        const result = fetcher.data;
        for (const product of result?.data ?? []) {
            const image = product.imageGroups?.[0]?.images?.[0];
            const url = image?.disBaseLink || image?.link;
            if (product.id && url) {
                map[product.id] = url;
            }
        }
        return map;
    }, [fetcher.data, idsKey]);
}
